import React, { useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import Input from 'src/components/Input/Input';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { KnowledgeBaseSelect } from 'src/components/KnowledgeBase/KnowledgeBaseSelect/KnowledgeBaseSelect';
import { useAddKnowledgeBaseDocumentMutation, useGetKnowledgeBaseQuery } from 'src/services';
import { Skeleton } from 'src/components/ui/skeleton';
import { useLoader } from 'src/provider/LoaderProvider';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import { useNavigate } from 'react-router-dom';
import { PlateEditor } from 'src/components/PlateEditor/PlateEditor';
import type { KnowledgeBase } from 'src/services/knowledgeBaseService';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { trimChar } from 'src/utils/trimChar';

const FAQIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M10.8 8.4V1.2H3.6V8.4H10.8ZM12 8.4C12 9.06 11.46 9.6 10.8 9.6H3.6C2.94 9.6 2.4 9.06 2.4 8.4V1.2C2.4 0.54 2.94 0 3.6 0H10.8C11.46 0 12 0.54 12 1.2V8.4ZM8.4 10.8V12H1.2C0.54 12 0 11.46 0 10.8V3H1.2V10.8H8.4ZM7.32 1.8C6.78 1.8 6.36 1.92 6.06 2.16C5.76 2.4 5.58 2.76 5.58 3.24H6.72C6.72 3.06 6.78 2.94 6.9 2.82C7.02 2.76 7.14 2.7 7.32 2.7C7.5 2.7 7.68 2.76 7.8 2.88C7.92 3 7.98 3.12 7.98 3.36C7.98 3.54 7.92 3.72 7.86 3.84C7.8 3.96 7.62 4.08 7.5 4.2C7.2 4.38 6.96 4.56 6.9 4.74C6.66 4.86 6.6 5.1 6.6 5.4H7.8C7.8 5.22 7.8 5.04 7.86 4.98C7.92 4.86 8.04 4.74 8.16 4.68C8.4 4.56 8.64 4.38 8.82 4.14C9 3.84 9.12 3.6 9.12 3.3C9.12 2.82 8.94 2.46 8.64 2.22C8.34 1.92 7.86 1.8 7.32 1.8ZM6.6 6V7.2H7.8V6H6.6Z"
      fill="#05050D"
      className={'opacity-30 group-hover/faq:opacity-100'}
    />
  </svg>
);

const FAQPopupTrigger = () => (
  <div
    className={
      'group/faq flex row items-center gap-[5px] hover:cursor-pointer text-ebony_opacity_30 text-[0.688rem] font-medium'
    }
  >
    <span className={'group-hover/faq:text-ebony'}>Add FAQ</span>
    <FAQIcon />
  </div>
);

interface FAQFormValues {
  question: string;
  answer: string;
  // The knowledge base select stores the numeric id.
  knowledgeBase: string | number;
}

export const AddFAQPopup = ({
  trigger = <FAQPopupTrigger />,
  disabled = false,
  message = '',
  projectId,
}: {
  trigger?: React.ReactNode;
  disabled?: boolean;
  // Prefills the question.
  message?: string;
  projectId: string;
}) => {
  const [open, setOpen] = useState(false);
  const [kbOptions, setKbOptions] = useState<KnowledgeBase[]>([]);
  const loader = useLoader();
  const navigate = useNavigate();
  const { data: knowledgeBaseDataByProjectId, isLoading: isLoadingKnowledgeBaseDataByProjectId } =
    useGetKnowledgeBaseQuery(
      {
        load_projects: false,
        page_size: 100,
        project_ids: projectId,
      },
      { refetchOnMountOrArgChange: true, skip: !open || !projectId },
    );
  const [addKnowledgeBaseDocument, { isLoading: isLoadingAddKnowledgeBaseDocument }] =
    useAddKnowledgeBaseDocumentMutation();

  const { handleSubmit, control, setError, setValue, reset } = useForm<FAQFormValues>({
    defaultValues: {
      question: '',
      answer: '',
      knowledgeBase: '',
    },
    resolver: formResolver(
      object({
        knowledgeBase: stringScheme({ required: false }),
        question: stringScheme({ required: true, minLength: 3 }).label('Topic/Question'),
        answer: stringScheme({ required: true, minLength: 3 }).label('Content/Answer'),
      }),
    ),
  });

  useLoaderWhile(isLoadingAddKnowledgeBaseDocument);

  useEffect(() => {
    if (knowledgeBaseDataByProjectId) {
      setKbOptions(knowledgeBaseDataByProjectId?.items || []);
    }
  }, [knowledgeBaseDataByProjectId]);

  useEffect(() => {
    if (open) {
      setValue('question', message);
    }
  }, [open]);

  const submit = (data: FAQFormValues) => {
    let kbId = data.knowledgeBase || kbOptions[0]?.id;

    if (!kbId) {
      return;
    }

    kbId = +kbId;

    if (data.answer.replace(/[\s\u200B-\u200D\uFEFF]/g, '').trim().length <= 1) {
      setError('answer', { message: 'Field Required' });
      return;
    }

    const sanitizedQuestion = trimChar(data.question, '/').trim();
    const questionWithExtension = `${sanitizedQuestion}.faq`;

    if (questionWithExtension.startsWith('.')) {
      setError('question', { message: 'Invalid Format' });
      return;
    }

    addKnowledgeBaseDocument({
      id: kbId,
      body: {
        name: questionWithExtension,
        text: data.answer,
        path: '/',
      },
    }).then((addKnowledgeBaseDocumentResult) => {
      if (!('error' in addKnowledgeBaseDocumentResult)) {
        reset();
        close();
      }
    });
  };

  const close = () => {
    setOpen(false);
    loader.hide();
  };

  const renderContent = () => {
    if (isLoadingKnowledgeBaseDataByProjectId) {
      return (
        <div className={'flex flex-col gap-5 items-center'}>
          <Skeleton className="w-full min-h-[50px]" />
          <Skeleton className="w-full min-h-[50px]" />
          <Skeleton className="w-full min-h-[350px]" />
          <Skeleton className="w-1/2 min-h-[50px]" />
        </div>
      );
    }

    if (kbOptions.length) {
      return (
        <div className="grid gap-4 py-4">
          <div className="content">
            <form id="create-new-document" onSubmit={handleSubmit(submit)}>
              <div className={'input-container'}>
                <KnowledgeBaseSelect name={'knowledgeBase'} control={control} options={kbOptions} />
              </div>
              <div className={'input-container mt-5'}>
                <Controller
                  control={control}
                  name="question"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      type={'text'}
                      label={'Topic/Question'}
                      placeholder={'Enter a question or FAQ name here'}
                      invalidMessage={error?.message}
                      value={value}
                      isValid={!error?.message}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
            </form>
            <div className={'input-container'}>
              <Controller
                control={control}
                name="answer"
                render={({ field: { onChange }, fieldState: { error } }) => (
                  <PlateEditor
                    label={'Content/Answer'}
                    initialValue={''}
                    onChange={onChange}
                    error={error}
                  />
                )}
              />
            </div>
            <div className="flex justify-center mt-5">
              <div className="flex gap-2.5">
                <Button variant="outline" type="button" className="min-w-24" onClick={close}>
                  Cancel
                </Button>
                <Button form={'create-new-document'} className="min-w-24">
                  Create
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={'flex flex-col gap-5 items-center justify-center'}>
        <p>You need to assign some knowledge base to this project</p>
        <div className="flex gap-2.5">
          <Button variant="outline" type="button" className="min-w-24" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => navigate(PATH_DASHBOARD.AI_KNOWLEDGE_BASE)} className="min-w-24">
            Go To Knowledge Bases
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        disabled={disabled}
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer outline-0')}
      >
        {trigger}
      </DialogTrigger>
      <DialogContent closeButtonHidden>
        <DialogHeader>
          {Boolean(kbOptions.length) && <DialogTitle>Add to Knowledge Base</DialogTitle>}
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};
